import React, { useState } from 'react';
import { CheckCircle, Zap, User, Mail, Phone, Building, BookOpen, MessageSquare, Shield, ArrowRight, Users, Clock, Star, CreditCard } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { loadRazorpayScript, createRazorpayOrder, RAZORPAY_KEY_ID, validatePromoCode } from '../lib/razorpay';
import type { RazorpayResponse } from '../lib/razorpay';
import toast from 'react-hot-toast';
import SEOHead from '../components/SEOHead';

const STREAMS = ['Engineering', 'Medical', 'Law', 'Management', 'Other'];
const EXAMS = ['JEE', 'NEET', 'CLAT', 'Other'];
const AMOUNT = 399;

interface FormState {
  fullName: string;
  email: string;
  phone: string;
  targetCollege: string;
  stream: string;
  exam: string;
  questions: string;
}

const initialForm: FormState = {
  fullName: '',
  email: '',
  phone: '',
  targetCollege: '',
  stream: '',
  exam: '',
  questions: '',
};

const StudentConnect: React.FC = () => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const finalAmount = Math.max(0, AMOUNT - discount);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleExamToggle = (exam: string) => {
    setForm(prev => ({ ...prev, exam: prev.exam === exam ? '' : exam }));
  };

  const validate = () => {
    if (!form.fullName.trim()) { toast.error('Please enter your full name'); return false; }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error('Please enter a valid email'); return false; }
    if (!form.phone.trim() || !/^\+?[\d\s\-]{10,}$/.test(form.phone)) { toast.error('Please enter a valid phone number'); return false; }
    if (!form.targetCollege.trim()) { toast.error('Please enter your target college'); return false; }
    if (!form.stream) { toast.error('Please select your stream'); return false; }
    if (!form.exam) { toast.error('Please select your exam'); return false; }
    if (!form.questions.trim()) { toast.error('Please enter your questions'); return false; }
    return true;
  };

  const handlePromoApply = async () => {
    if (!promoInput.trim()) { toast.error('Please enter a promo code'); return; }
    const result = await validatePromoCode(promoInput.trim(), 'student_connect');
    if (result.isValid) {
      setDiscount(Math.min(result.discount, AMOUNT));
      setPromoApplied(true);
      toast.success(`Promo code applied! You saved ₹${Math.min(result.discount, AMOUNT)}`);
    } else {
      toast.error('Invalid or expired promo code');
    }
  };

  const handlePromoRemove = () => {
    setPromoInput('');
    setPromoApplied(false);
    setDiscount(0);
    toast.success('Promo code removed');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        setLoading(false);
        return;
      }

      if (finalAmount === 0) {
        const { error: freeInsertError } = await supabase
          .from('student_connect_requests')
          .insert({
            full_name: form.fullName.trim(),
            email: form.email.trim().toLowerCase(),
            phone: form.phone.trim(),
            target_college: form.targetCollege.trim(),
            stream: form.stream,
            exam: form.exam,
            questions: form.questions.trim(),
            status: 'pending',
            payment_status: 'completed',
            amount: 0,
            promo_code: promoInput.trim(),
            discount_amount: discount,
          });
        if (freeInsertError) throw freeInsertError;
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setLoading(false);
        return;
      }

      const order = await createRazorpayOrder(finalAmount);

      const { data: insertData, error: insertError } = await supabase
        .from('student_connect_requests')
        .insert({
          full_name: form.fullName.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          target_college: form.targetCollege.trim(),
          stream: form.stream,
          exam: form.exam,
          questions: form.questions.trim(),
          status: 'pending',
          payment_status: 'pending',
          razorpay_order_id: order.id,
          amount: finalAmount,
          promo_code: promoApplied ? promoInput.trim() : null,
          discount_amount: discount,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      const submissionId = insertData.id;

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Grad Vikalp',
        description: 'Student Connect — Mentor Matching',
        order_id: order.id,
        prefill: {
          name: form.fullName.trim(),
          email: form.email.trim().toLowerCase(),
          contact: form.phone.trim(),
        },
        theme: { color: '#0284c7' },
        handler: async (response: RazorpayResponse) => {
          try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

            const verifyRes = await fetch(`${supabaseUrl}/functions/v1/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${supabaseKey}`,
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                submission_id: submissionId,
                table_name: 'student_connect_requests',
              }),
            });

            const result = await verifyRes.json();

            if (result.success) {
              setSubmitted(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              toast.error('Payment verification failed. Please contact support.');
            }
          } catch {
            toast.error('Payment verification error. Please contact support.');
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.error('Payment cancelled.');
            setLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        toast.error('Payment failed. Please try again.');
        setLoading(false);
      });
      rzp.open();
    } catch (err: any) {
      console.error('Submission error:', err);
      toast.error(err?.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Student Connect — Get Guidance from Real Students | Grad Vikalp"
        description="Connect with a verified student mentor from your dream college within 48 hours for just ₹399. Real guidance from real students."
      />

      <div style={{ background: 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 40%, #e8f5e9 100%)', minHeight: '100vh' }}>

        {/* Hero Section */}
        <section className="pt-5 pb-4 px-3">
          <div className="container">
            <div className="text-center mb-4">
              <div className="d-inline-flex align-items-center gap-2 bg-white rounded-pill px-3 py-2 mb-3 shadow-sm" style={{ border: '1.5px solid #bae6fd' }}>
                <Users size={16} style={{ color: '#0284c7' }} />
                <br></br>
                <br></br>
                <br></br>
                <br></br>
                <span style={{ color: '#0284c7', fontWeight: 600, fontSize: '0.85rem' }}>Real Students · Real Guidance</span>
              </div>
              <h1 className="fw-bold mb-3" style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', color: '#0f172a', lineHeight: 1.2 }}>
                Get Guidance from<br />
                <span style={{ color: '#0284c7' }}>Real Students</span>
              </h1>
              <p className="text-muted mx-auto mb-4" style={{ maxWidth: 520, fontSize: '1.1rem', lineHeight: 1.6 }}>
                Tell us about yourself and we'll connect you with a verified mentor from your dream college within 48 hours — for ₹399 or will intitiate a full refund within 24hr.
              </p>

              {/* 3-step flow */}
              <div className="d-flex align-items-center justify-content-center flex-wrap gap-0 mb-4">
                {[
                  { num: 1, label: 'Fill the Form' },
                  { num: 2, label: 'Pay ₹399' },
                  { num: 3, label: 'Connect in 48hrs' },
                ].map((step, i) => (
                  <React.Fragment key={step.num}>
                    <div className="d-flex flex-column align-items-center" style={{ minWidth: 100 }}>
                      <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold text-white mb-2"
                        style={{ width: 44, height: 44, background: 'linear-gradient(135deg, #0284c7, #0ea5e9)', fontSize: '1.1rem', boxShadow: '0 4px 12px rgba(2,132,199,0.35)' }}>
                        {step.num}
                      </div>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>{step.label}</span>
                    </div>
                    {i < 2 && (
                      <div style={{ width: 48, height: 2, background: 'linear-gradient(to right, #0284c7, #7dd3fc)', margin: '0 4px 20px' }} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Guarantee badge */}
              <div className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold"
                style={{ border: '2px solid #16a34a', color: '#16a34a', background: '#f0fdf4', fontSize: '0.9rem' }}>
                <CheckCircle size={17} />
                48-Hour Match
              </div>
            </div>
          </div>
        </section>

        {/* Form / Success Section */}
        <section className="pb-5 px-3">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-12 col-lg-8 col-xl-7">

                {submitted ? (
                  /* Success State */
                  <div className="bg-white rounded-4 shadow-lg p-5 text-center" style={{ border: '1px solid #e2e8f0' }}>
                    <div className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-4"
                      style={{ width: 80, height: 80, background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}>
                      <CheckCircle size={40} style={{ color: '#16a34a' }} />
                    </div>
                    <h2 className="fw-bold mb-2" style={{ color: '#0f172a', fontSize: '1.75rem' }}>Payment Successful!</h2>
                    <p className="text-muted mb-4" style={{ fontSize: '1rem', maxWidth: 420, margin: '0 auto 1.5rem' }}>
                      Your request has been received. We'll match you with a verified student mentor from your target college and schedule a meeting within <strong>48 hours</strong>. Check your email (both primary & spam )for confirmation.
                    </p>

                    <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                      {[
                        { icon: '✅', text: 'Payment Confirmed' },
                        { icon: '📧', text: 'Confirmation Email Sent' },
                        { icon: '⏳', text: 'Mentor Match in 48hrs else Refund' },
                      ].map(tag => (
                        <span key={tag.text} className="d-flex align-items-center gap-1 px-3 py-2 rounded-pill fw-medium"
                          style={{ background: '#f1f5f9', color: '#334155', fontSize: '0.85rem', border: '1px solid #e2e8f0' }}>
                          <span>{tag.icon}</span>
                          <span>{tag.text}</span>
                        </span>
                      ))}
                    </div>

                    <button
                      onClick={() => { setSubmitted(false); setForm(initialForm); }}
                      className="btn btn-outline-primary rounded-pill px-4"
                    >
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  /* Form Card */
                  <div className="bg-white rounded-4 shadow-lg overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
                    {/* Card Header */}
                    <div className="d-flex align-items-center justify-content-between px-4 py-3"
                      style={{ background: 'linear-gradient(135deg, #0284c7, #0369a1)' }}>
                      <div className="d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.2)' }}>
                          <Users size={18} className="text-white" />
                        </div>
                        <span className="text-white fw-bold" style={{ fontSize: '1.05rem' }}>Connect with a Student Mentor</span>
                      </div>
                      <div className="d-flex align-items-center gap-1 px-3 py-1 rounded-pill fw-bold"
                        style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: '0.8rem', border: '1.5px solid rgba(255,255,255,0.35)' }}>
                        <Zap size={13} />
                        48hrs Match
                      </div>
                    </div>

                    {/* Form Body */}
                    <form onSubmit={handleSubmit} className="p-4 p-md-5">
                      {/* Row 1: Name + Email */}
                      <div className="row g-3 mb-3">
                        <div className="col-12 col-sm-6">
                          <label className="form-label fw-semibold" style={{ color: '#374151', fontSize: '0.875rem' }}>
                            <User size={14} className="me-1" style={{ color: '#0284c7' }} />Full Name
                          </label>
                          <input
                            type="text"
                            name="fullName"
                            value={form.fullName}
                            onChange={handleChange}
                            className="form-control rounded-3"
                            placeholder="Rahul Sharma"
                            style={{ border: '1.5px solid #e2e8f0', padding: '10px 14px', fontSize: '0.95rem' }}
                          />
                        </div>
                        <div className="col-12 col-sm-6">
                          <label className="form-label fw-semibold" style={{ color: '#374151', fontSize: '0.875rem' }}>
                            <Mail size={14} className="me-1" style={{ color: '#0284c7' }} />Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            className="form-control rounded-3"
                            placeholder="rahul@example.com"
                            style={{ border: '1.5px solid #e2e8f0', padding: '10px 14px', fontSize: '0.95rem' }}
                          />
                        </div>
                      </div>

                      {/* Row 2: Phone + Target College */}
                      <div className="row g-3 mb-3">
                        <div className="col-12 col-sm-6">
                          <label className="form-label fw-semibold" style={{ color: '#374151', fontSize: '0.875rem' }}>
                            <Phone size={14} className="me-1" style={{ color: '#0284c7' }} />Phone Number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            className="form-control rounded-3"
                            placeholder="+91 98765 43210"
                            style={{ border: '1.5px solid #e2e8f0', padding: '10px 14px', fontSize: '0.95rem' }}
                          />
                        </div>
                        <div className="col-12 col-sm-6">
                          <label className="form-label fw-semibold" style={{ color: '#374151', fontSize: '0.875rem' }}>
                            <Building size={14} className="me-1" style={{ color: '#0284c7' }} />Target College
                          </label>
                          <input
                            type="text"
                            name="targetCollege"
                            value={form.targetCollege}
                            onChange={handleChange}
                            className="form-control rounded-3"
                            placeholder="IIT Bombay / AIIMS Delhi"
                            style={{ border: '1.5px solid #e2e8f0', padding: '10px 14px', fontSize: '0.95rem' }}
                          />
                        </div>
                      </div>

                      {/* Stream Dropdown */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold" style={{ color: '#374151', fontSize: '0.875rem' }}>
                          <BookOpen size={14} className="me-1" style={{ color: '#0284c7' }} />Stream
                        </label>
                        <select
                          name="stream"
                          value={form.stream}
                          onChange={handleChange}
                          className="form-select rounded-3"
                          style={{ border: '1.5px solid #e2e8f0', padding: '10px 14px', fontSize: '0.95rem', color: form.stream ? '#0f172a' : '#94a3b8' }}
                        >
                          <option value="">Select your stream</option>
                          {STREAMS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>

                      {/* Exam Pill Selector */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold" style={{ color: '#374151', fontSize: '0.875rem' }}>
                          <Star size={14} className="me-1" style={{ color: '#0284c7' }} />Exam
                        </label>
                        <div className="d-flex flex-wrap gap-2">
                          {EXAMS.map(exam => {
                            const active = form.exam === exam;
                            return (
                              <button
                                key={exam}
                                type="button"
                                onClick={() => handleExamToggle(exam)}
                                className="btn rounded-pill fw-semibold"
                                style={{
                                  padding: '7px 22px',
                                  fontSize: '0.875rem',
                                  border: active ? '2px solid #0284c7' : '1.5px solid #e2e8f0',
                                  background: active ? 'linear-gradient(135deg, #0284c7, #0369a1)' : '#f8fafc',
                                  color: active ? '#fff' : '#64748b',
                                  transition: 'all 0.18s ease',
                                  boxShadow: active ? '0 4px 12px rgba(2,132,199,0.3)' : 'none',
                                  transform: active ? 'scale(1.04)' : 'scale(1)',
                                }}
                              >
                                {exam}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Questions Textarea */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold" style={{ color: '#374151', fontSize: '0.875rem' }}>
                          <MessageSquare size={14} className="me-1" style={{ color: '#0284c7' }} />Your Questions
                        </label>
                        <textarea
                          name="questions"
                          value={form.questions}
                          onChange={handleChange}
                          className="form-control rounded-3"
                          rows={4}
                          placeholder="What do you want to know? e.g. What is campus life like? How is the placement? Which branch should I pick?"
                          style={{ border: '1.5px solid #e2e8f0', padding: '12px 14px', fontSize: '0.95rem', resize: 'vertical' }}
                        />
                      </div>

                      {/* Promo Code */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold" style={{ color: '#374151', fontSize: '0.875rem' }}>
                          Have a Promo Code?
                        </label>
                        {promoApplied ? (
                          <div className="d-flex align-items-center gap-2 p-2 rounded-3" style={{ background: '#f0fdf4', border: '1.5px solid #86efac' }}>
                            <CheckCircle size={16} style={{ color: '#16a34a' }} />
                            <span className="fw-semibold" style={{ color: '#16a34a', fontSize: '0.9rem' }}>{promoInput.toUpperCase()} — ₹{discount} off</span>
                            <button type="button" onClick={handlePromoRemove} className="btn btn-sm ms-auto" style={{ color: '#dc2626', padding: '2px 8px', fontSize: '0.8rem' }}>Remove</button>
                          </div>
                        ) : (
                          <div className="input-group">
                            <input
                              type="text"
                              value={promoInput}
                              onChange={e => setPromoInput(e.target.value)}
                              className="form-control rounded-3"
                              placeholder="Enter promo code"
                              style={{ border: '1.5px solid #e2e8f0', fontSize: '0.95rem' }}
                              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handlePromoApply())}
                            />
                            <button type="button" onClick={handlePromoApply} className="btn btn-outline-primary ms-2 rounded-3" style={{ fontSize: '0.875rem' }}>Apply</button>
                          </div>
                        )}
                      </div>

                      {/* Payment Summary */}
                      <div className="rounded-3 p-3 mb-4" style={{ background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)', border: '1.5px solid #bae6fd' }}>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <CreditCard size={18} style={{ color: '#0284c7' }} />
                            <div>
                              <div className="fw-bold" style={{ color: '#0f172a', fontSize: '0.9rem' }}>One-time Payment</div>
                              <div style={{ color: '#64748b', fontSize: '0.78rem' }}>Secure payment via Razorpay</div>
                            </div>
                          </div>
                          <div>
                            {promoApplied && <div className="text-end text-decoration-line-through" style={{ color: '#94a3b8', fontSize: '0.9rem' }}>₹{AMOUNT}</div>}
                            <div className="fw-bold text-end" style={{ color: '#0284c7', fontSize: '1.4rem' }}>{finalAmount === 0 ? 'FREE' : `₹${finalAmount}`}</div>
                          </div>
                        </div>
                        {promoApplied && (
                          <div className="d-flex justify-content-between" style={{ color: '#16a34a', fontSize: '0.82rem' }}>
                            <span>Discount ({promoInput.toUpperCase()})</span>
                            <span>-₹{discount}</span>
                          </div>
                        )}
                      </div>

                      {/* Bottom Row: Privacy + Submit */}
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2" style={{ color: '#64748b', fontSize: '0.82rem' }}>
                          <Shield size={15} style={{ color: '#16a34a' }} />
                          <span>Your info is safe &amp; never shared</span>
                        </div>
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn fw-bold d-flex align-items-center gap-2 rounded-pill"
                          style={{
                            background: loading ? '#93c5fd' : 'linear-gradient(135deg, #0284c7, #0369a1)',
                            color: '#fff',
                            padding: '11px 28px',
                            fontSize: '0.95rem',
                            border: 'none',
                            boxShadow: loading ? 'none' : '0 4px 16px rgba(2,132,199,0.4)',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm" />
                              Processing...
                            </>
                          ) : (
                            <>
                              {finalAmount === 0 ? 'Claim Free & Find My Mentor' : `Pay ₹${finalAmount} & Find My Mentor`}
                              <ArrowRight size={17} />
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Trust indicators below form */}
                {!submitted && (
                  <div className="row g-3 mt-3">
                    {[
                      { icon: <CheckCircle size={20} style={{ color: '#16a34a' }} />, title: 'Mentors', desc: 'Verified students from top colleges' },
                      { icon: <Clock size={20} style={{ color: '#0284c7' }} />, title: '48hr Guarantee', desc: 'Matched or your money back' },
                      { icon: <Star size={20} style={{ color: '#f59e0b' }} />, title: 'Rating', desc: 'From happy students' },
                    ].map(item => (
                      <div key={item.title} className="col-12 col-sm-4">
                        <div className="bg-white rounded-3 p-3 text-center shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                          <div className="mb-1">{item.icon}</div>
                          <div className="fw-bold" style={{ color: '#0f172a', fontSize: '0.9rem' }}>{item.title}</div>
                          <div style={{ color: '#64748b', fontSize: '0.78rem' }}>{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default StudentConnect;
